import React from "react";
import { Link } from "react-router-dom";
import { trackMarketingFunnel } from "../../utils/marketingAnalytics";

const ZapIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-blue-400">
    <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" />
  </svg>
);
const ArrowRightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-2 w-5 h-5">
    <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
  </svg>
);
const TruckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-blue-400">
    <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" /><path d="M15 18H9" /><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" /><circle cx="17" cy="18" r="2" /><circle cx="7" cy="18" r="2" />
  </svg>
);
const RouteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-cyan-400">
    <circle cx="6" cy="19" r="3" /><path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15" /><circle cx="18" cy="5" r="3" />
  </svg>
);
const BatteryIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-amber-400">
    <rect x="2" y="7" width="18" height="10" rx="2" ry="2" /><line x1="6" y1="11" x2="6" y2="13" /><line x1="10" y1="11" x2="10" y2="13" /><line x1="14" y1="11" x2="14" y2="13" />
  </svg>
);
const MapPinIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-emerald-400">
    <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" /><circle cx="12" cy="10" r="3" />
  </svg>
);
const ChartIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 text-indigo-400">
    <path d="M3 3v18h18" /><path d="m19 9-5 5-4-4-3 3" />
  </svg>
);
const CircleCheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-blue-500 flex-shrink-0">
    <circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" />
  </svg>
);

const FEATURES = [
  { icon: RouteIcon, iconBg: "bg-blue-500/10", title: "Route and duty-cycle modeling", desc: "Evaluate electrification readiness by routes with operational constraints." },
  { icon: BatteryIcon, iconBg: "bg-amber-500/10", title: "Charging strategy optimization", desc: "Compare depot and public charging strategies to lower cost and risk." },
  { icon: MapPinIcon, iconBg: "bg-emerald-500/10", title: "Infrastructure planning", desc: "Plan site capacity, phasing, and charging deployment aligned with growth." },
  { icon: ChartIcon, iconBg: "bg-cyan-500/10", title: "Financial decision support", desc: "Get TCO, scenario analysis, and executive-ready outputs for internal approval." },
];

const STATS = [
  { value: "30%", label: "Cost Reduction", desc: "Save on fuel and infrastructure" },
  { value: "Zero", label: "Emissions", desc: "Meet sustainability goals" },
  { value: "Unlimited", label: "Scenario Analysis", desc: "Explore diverse strategies" },
];

const FAQ = [
  { q: "Is this only for large fleets?", a: "No. Teams often start with one depot or a pilot route and scale with the same workflow." },
  { q: "Do we need perfect data to begin?", a: "No. You can start with baseline route and vehicle inputs, then refine scenarios over time." },
  { q: "Can we compare charging strategies?", a: "Yes. The platform supports side-by-side scenario comparison for cost and operations." },
];

const USE_CASES = [
  {
    title: "Drayage Fleets",
    desc: "Plan routes for short-haul drayage operations with class 8 trucks. Perfect for moving containers from port to warehouse.",
    features: ["Route optimization", "Charging station mapping", "Depot management"],
    img: "/img/solutions/fleet-drayage.jpg",
  },
  {
    title: "Delivery Fleets",
    desc: "Optimize last-mile delivery operations with electric vans and trucks. Ideal for logistics providers managing frequent stops.",
    features: ["Multi-depot coordination", "Extensive charging networks", "Vehicle model selection"],
    img: "/img/solutions/fleet-delivery.jpg",
  },
  {
    title: "Municipal Fleets",
    desc: "Manage public service vehicles including buses, waste collection, and emergency services with electric alternatives.",
    features: ["Public charging integration", "Service route planning", "Environmental reporting"],
    img: "/img/solutions/fleet-municipal.jpeg",
  },
];

function FleetOwners() {
  return (
    <div className="min-h-screen bg-white">
      <section className="relative pt-40 pb-24 overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/40 to-teal-50/30">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-200/50 via-transparent to-transparent" />
          <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-blue-200/50 rounded-full blur-3xl" />
        </div>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent via-white/60 to-white" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            {/* <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 border border-blue-200 mb-8">
              <ZapIcon />
              <span className="text-sm text-blue-700 font-medium">eFleetPlan</span>
            </div> */}
            <h1 className="text-5xl md:text-7xl font-bold text-slate-900 mb-6">
              Electrify your fleet with
              <span className="block pb-3 bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">smart planning</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed mb-10 max-w-xl">
              AmpTrans eFleetPlan<sup>TM</sup> helps you transition to electric vehicles with intelligent route optimization, charging infrastructure analysis, vehicle model selection, and more.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/subscription?module=fleet"
                onClick={() => trackMarketingFunnel("fleet_detail_primary_cta_click")}
                className="inline-flex items-center justify-center gap-2 font-medium bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-8 py-6 text-lg rounded-xl shadow-lg shadow-blue-500/25 hover:scale-105 transition-all"
              >
                Start Free Trial
                <ArrowRightIcon />
              </Link>
              <Link
                to="/contact"
                onClick={() => trackMarketingFunnel("fleet_detail_secondary_cta_click")}
                className="inline-flex items-center justify-center gap-2 font-medium border border-slate-300 text-slate-700 hover:bg-slate-100 px-8 py-6 text-lg rounded-xl transition-colors"
              >
                Contact Sales
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {STATS.map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent mb-2">{s.value}</div>
                <div className="text-slate-900 font-semibold text-lg mb-1">{s.label}</div>
                <div className="text-slate-500 text-sm">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Core capabilities</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">Focused tools for transition planning, charging decisions, and financial confidence.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="group bg-slate-50 hover:bg-white border border-slate-100 hover:border-slate-200 rounded-2xl p-6 hover:shadow-lg transition-all duration-300">
                  <div className={`w-12 h-12 rounded-xl ${f.iconBg} flex items-center justify-center mb-4`}>
                    <Icon />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{f.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quick estimate entry */}
      <section className="relative overflow-hidden bg-slate-50 py-16">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-white via-white/70 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent via-white/60 to-white" />
        <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8">
          <div className="rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 to-cyan-50 p-8 md:p-10">
            <div className="flex items-center gap-2 text-blue-700 mb-4">
              <ZapIcon />
              <span className="text-xs font-semibold uppercase tracking-[0.2em]">Quick start</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">
              Start with a 2-minute EV vs diesel/gas baseline
            </h2>
            <p className="text-slate-600 leading-relaxed max-w-3xl">
              Use our free baseline estimator for a fast single-vehicle comparison, then move into eFleetPlan for
              route feasibility, charging strategy, and fleet-wide deployment planning.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Link
                to="/fleet-planning-basic"
                onClick={() => trackMarketingFunnel("fleet_detail_quick_estimate_click")}
                className="inline-flex items-center justify-center gap-2 font-medium bg-white border border-blue-200 text-blue-700 hover:bg-blue-50 px-6 py-3 rounded-xl transition-colors"
              >
                Try free baseline estimate
                <ArrowRightIcon />
              </Link>
              <Link
                to="/subscription?module=fleet"
                onClick={() => trackMarketingFunnel("fleet_detail_quick_estimate_upgrade_click")}
                className="inline-flex items-center justify-center gap-2 font-medium bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl transition-colors"
              >
                Go directly to eFleetPlan
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="relative overflow-hidden bg-slate-50 py-24">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-white via-white/70 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent via-white/60 to-white" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Perfect for Every Fleet</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">Whether you&apos;re a small delivery service or a large logistics company, our platform scales to meet your needs.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {USE_CASES.map((item) => (
              <div key={item.title} className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-lg transition-all duration-300">
                <div className="relative h-48 overflow-hidden">
                  <img src={item.img} alt={item.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                  <h3 className="absolute bottom-4 left-5 text-xl font-bold text-white">{item.title}</h3>
                </div>
                <div className="p-6">
                  <p className="text-slate-600 mb-4 leading-relaxed text-sm">{item.desc}</p>
                  <ul className="space-y-2">
                    {item.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-slate-700 text-sm">
                        <CircleCheckIcon />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">FAQ</h2>
          <div className="space-y-4">
            {FAQ.map((item) => (
              <div key={item.q} className="rounded-xl border border-slate-200 p-5">
                <h3 className="font-semibold text-slate-900 mb-2">{item.q}</h3>
                <p className="text-sm text-slate-600">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-600/10 via-transparent to-transparent" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Ready to Electrify Your Fleet?</h2>
          <p className="text-slate-400 mb-10">Start with a free plan · Cancel anytime · Custom plans available</p>
          <Link
            to="/subscription?module=fleet"
            onClick={() => trackMarketingFunnel("fleet_detail_final_cta_click")}
            className="inline-flex items-center justify-center gap-2 font-medium h-10 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-10 py-6 text-lg rounded-xl shadow-lg shadow-blue-500/25"
          >
            Start Free Today
            <ArrowRightIcon />
          </Link>
        </div>
      </section>
    </div>
  );
}

export default FleetOwners;
